import * as api from '@apis/admin-user-contents';
import { UserContentDetailCard } from '@components/user-contents/UserContentDetailCard';
import { useAuthentication } from '@hooks/authentication';
import { ExtendedUserContentComment } from '@models/user-content-comment';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Input, Modal, Select, Table } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

export const CommentMonitoringPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<{
    page: number;
    size: number;
    status?: string;
    keyword?: string;
    hasReports?: boolean;
  }>({
    page: 0,
    size: 20,
  });
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedComment, setSelectedComment] = useState<ExtendedUserContentComment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showContent, setShowContent] = useState<any>();

  useAuthentication();

  const commentsQuery = useQuery({
    queryKey: [`admin-comments`, JSON.stringify(searchQuery)],
    queryFn: () => api.getAllComments(searchQuery),
  });

  const updateCommentStatusMutation = useMutation({
    mutationFn: ({ commentId, status }: { commentId: string; status: string }) =>
      api.updateCommentStatus(commentId, status),
    onSuccess: async () => {
      toast.success('댓글 상태가 변경되었습니다.');
      commentsQuery.refetch();
    },
    onError: () => {
      toast.error('댓글 상태 변경에 실패했습니다.');
    },
  });

  const comments = commentsQuery.data?.data;

  const TABLE_COLUMNS: ColumnsType<ExtendedUserContentComment> = [
    {
      title: '댓글ID',
      dataIndex: '_id',
      width: 100,
      render: (_id) => {
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(_id);
              toast.success('댓글 ID를 복사했습니다.');
            }}>
            복사
          </Button>
        );
      },
    },
    {
      title: '컨텐츠ID',
      width: 100,
      render: (record) => {
        const userContentId = record.userContentId;
        if (!userContentId) {
          return null;
        }
        return (
          <div>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(userContentId);
                toast.success('컨텐츠 ID를 복사했습니다.');
              }}>
              복사
            </Button>
          </div>
        );
      },
    },
    {
      title: '컨텐츠정보',
      width: 200,
      render: (record) => {
        const userContent = record.userContent;
        if (!userContent) {
          return null;
        }
        let board = userContent?.board;
        switch (userContent?.board) {
          case 'review':
            board = '후기';
            break;
          case 'curation':
            board = '콘텐츠발견';
            break;
          case 'partner-news':
            board = '선생님소식';
            break;
          case 'vote':
            board = '투표';
            break;
          case 'talk':
            board = '톡톡';
            break;
        }
        return (
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{board}</div>
            <div>{userContent?.title || '-'}</div>
            <Button
              onClick={() => {
                setShowContent(<UserContentDetailCard userContent={userContent} />);
              }}>
              컨텐츠 보기
            </Button>
          </div>
        );
      },
    },
    {
      title: '작성자',
      width: 150,
      render: (record: ExtendedUserContentComment) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ fontWeight: 'bold' }}>{record.editor?.profile?.nickname || '탈퇴한 사용자'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {record.editor?.profile?.name && <div style={{ fontSize: '14px' }}>{record.editor.profile.name}</div>}
              {record.editor?.profile?.phone && <div style={{ fontSize: '14px' }}>({record.editor.profile.phone})</div>}
            </div>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(record.editorId || '');
                toast.success('댓글 작성자 ID를 복사했습니다.');
              }}>
              ID 복사
            </Button>
          </div>
        );
      },
    },
    {
      title: '댓글 내용',
      dataIndex: 'content',
      width: 400,
      render: (content) => {
        return <div style={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflow: 'auto' }}>{content}</div>;
      },
    },
    {
      title: '깊이',
      dataIndex: 'depth',
      width: 80,
      render: (depth) => {
        return depth === 0 ? '원댓글' : `대댓글(${depth})`;
      },
    },
    {
      title: '신고',
      width: 100,
      render: (record: ExtendedUserContentComment) => {
        const reportCount = record.reports?.length || 0;
        return (
          <div
            style={{
              color: reportCount > 0 ? '#FF4D4F' : '#999',
              fontWeight: reportCount > 0 ? 600 : 400,
            }}>
            {reportCount > 0 ? `${reportCount}건` : '없음'}
          </div>
        );
      },
    },
    {
      title: '상태',
      width: 120,
      render: (record: ExtendedUserContentComment) => {
        return (
          <Select
            style={{ width: '100px' }}
            value={record.status}
            options={[
              {
                value: 'active',
                label: '활성',
              },
              {
                value: 'hidden',
                label: '숨김',
              },
              {
                value: 'deleted',
                label: '삭제',
              },
            ]}
            onChange={(value) => {
              updateCommentStatusMutation.mutate({ commentId: record._id, status: value });
            }}
          />
        );
      },
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      width: 150,
      render: (createdAt) => {
        return moment(createdAt).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      title: '상세',
      width: 80,
      render: (record: ExtendedUserContentComment) => {
        return (
          <Button
            onClick={() => {
              setSelectedComment(record);
              setIsModalOpen(true);
            }}>
            보기
          </Button>
        );
      },
    },
  ];

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>댓글 모니터링</div>
      </div>
      <div className="page-content">
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
          <FilterSelect
            inactiveValue="all"
            defaultValue="all"
            style={{ width: 160 }}
            onChange={(value) => {
              if (value === 'all') {
                setSearchQuery({ ...searchQuery, status: undefined, hasReports: undefined, page: 0 });
              } else if (value === 'reported') {
                setSearchQuery({ ...searchQuery, hasReports: true, page: 0 });
              } else {
                setSearchQuery({ ...searchQuery, status: value, hasReports: undefined, page: 0 });
              }
            }}
            options={[
              { value: 'all', label: '전체' },
              { value: 'reported', label: '신고된 댓글' },
              { value: 'active', label: '활성 댓글' },
              { value: 'hidden', label: '숨김 댓글' },
              { value: 'deleted', label: '삭제 댓글' },
            ]}
          />
          <Input
            className={filterActiveClassName(!!searchQuery.keyword)}
            style={{ width: '240px' }}
            value={searchKeyword}
            placeholder="검색어 입력 후 엔터"
            onChange={(e) => {
              setSearchKeyword(e.target.value);
            }}
            onKeyUp={(e) => {
              if (e.code === 'Enter') {
                setSearchQuery({ ...searchQuery, keyword: searchKeyword, page: 0 });
              }
            }}
          />
          <Button
            onClick={() => {
              setSearchKeyword('');
              setSearchQuery({ page: 0, size: 20 });
            }}>
            검색 초기화
          </Button>
        </div>
        <Table
          columns={TABLE_COLUMNS}
          scroll={{ x: 'max-content', y: 'calc(100vh - 280px)' }}
          rowKey={(record) => record._id}
          dataSource={comments?.contents || []}
          pagination={{
            current: (comments?.page || 0) + 1,
            pageSize: comments?.size || 20,
            total: comments?.total || 0,
          }}
          loading={commentsQuery.isLoading}
          onChange={(pagination) => {
            setSearchQuery({
              ...searchQuery,
              page: (pagination.current || 2) - 1,
              size: pagination.pageSize || 20,
            });
          }}
        />
      </div>
      <Modal
        title="댓글 상세"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedComment(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsModalOpen(false);
              setSelectedComment(null);
            }}>
            닫기
          </Button>,
        ]}
        width={600}>
        {selectedComment && (
          <div style={{ padding: '20px 0' }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>작성자:</strong> {selectedComment.editor?.profile?.nickname || '탈퇴한 사용자'}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>내용:</strong>
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: '#F5F7FB',
                  borderRadius: '4px',
                }}>
                {selectedComment.content}
              </div>
            </div>
            {selectedComment.originalContent && selectedComment.status !== 'active' && (
              <div style={{ marginBottom: '12px' }}>
                <strong>원본 내용:</strong>
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    marginTop: '8px',
                    padding: '12px',
                    backgroundColor: '#FFF7E6',
                    borderRadius: '4px',
                  }}>
                  {selectedComment.originalContent}
                </div>
              </div>
            )}
            <div style={{ marginBottom: '12px' }}>
              <strong>상태:</strong>{' '}
              {selectedComment.status === 'active' ? '활성' : selectedComment.status === 'hidden' ? '숨김' : '삭제'}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>깊이:</strong> {selectedComment.depth === 0 ? '원댓글' : `대댓글(${selectedComment.depth})`}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>생성일:</strong> {moment(selectedComment.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </div>
            {selectedComment.reports && selectedComment.reports.length > 0 && (
              <div>
                <strong>신고 내역 ({selectedComment.reports.length}건):</strong>
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedComment.reports.map((report: any, idx: number) => (
                    <div
                      key={idx}
                      style={{
                        padding: '12px',
                        backgroundColor: '#FFF1F0',
                        borderRadius: '4px',
                      }}>
                      <div style={{ marginBottom: '4px' }}>
                        <strong>사유:</strong> {report.reason}
                      </div>
                      {report.reasonDetail && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>상세:</strong> {report.reasonDetail}
                        </div>
                      )}
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        {moment(report.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      <Modal
        open={!!showContent}
        onOk={() => {
          setShowContent(undefined);
        }}
        onCancel={() => {
          setShowContent(undefined);
        }}>
        <div style={{ height: '36px' }}></div>
        <OverlayScrollbarsComponent
          defer
          options={{
            scrollbars: { autoHide: 'scroll' },
            overflow: {
              x: 'hidden',
            },
          }}
          style={{
            backgroundColor: '#ffffff',
            marginTop: 0,
            position: 'relative',
            overflow: 'auto',
            width: '100%',
            height: 'calc(100dvh - 280px)',
          }}>
          {showContent}
        </OverlayScrollbarsComponent>
      </Modal>
    </div>
  );
};

export default CommentMonitoringPage;
